import React from "react";
import { useTheme } from "@mui/styles";
import { Typography, Box, useMediaQuery } from "@mui/material";
import json2mq from "json2mq";
import AdmissionProcessButton from "../Buttons/AdmissionProcessButton";
import { useLocation } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";

const useStyles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "center" },
    width: { xs: "100%" },
    mt: { xs: "134px" },
  },
  child: {
    display: "flex",
    flexDirection: { xs: "column" },
    alignItems: { xs: "center", md: "flex-start" },
    mt: { xs: "117px", md: 0, lg: 0, xl: "45px" },
    height: { md: "437px", lg: "582px", xl: "729px" },
    ml: { md: "40px", lg: "100px", xl: "107px" },
  },
  titleContainer: {
    display: { md: "flex" },
    justifyContent: { md: "center" },
    alignItems: { md: "center" },
  },
  title: {
    fontSize: { xs: "14px", md: "16px" },
    letterSpacing: { xs: "1.4px", md: "1.6px" },
    fontFamily: theme.typography.montserrat.bold,
    color: theme.palette.red.main,
  },
  subtitle: {
    maxWidth: { xs: "225px", md: "420px", lg: "540px", xl: "676px" },
    height: { xs: "72px", md: "74px", lg: "76px", xl: "79px" },
    color: theme.palette.blue.main,
    fontSize: { xs: "32px", md: "35px", lg: "35px", xl: "65px" },
    lineHeight: { xs: "36px", md: "normal", lg: "normal", xl: "75px" },
    mt: { xs: "16px", md: "19px", lg: "22px", xl: "27px" },
    fontFamily: theme.typography.montserrat.bold,
    textAlign: { xs: "center", md: "start" },
  },
  firstCard: {
    mt: { xs: "65px", md: 0, lg: 0, xl: "43px" },
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "center" },
  },
  text: {
    fontSize: { xs: "24px", md: "17px", lg: "24px", xl: "24px" },
    lineHeight: { xs: "36px", md: "30px", lg: "32px", xl: "36px" },
    fontFamily: theme.typography.anaHeim.regular,
    color: theme.palette.blue.main,
    width: { xs: "285px", md: "350px", lg: "515px", xl: "518px" },
    textAlign: { xs: "center", md: "start" },
    ml: { md: "13px" },
    pt: { xl: "35px" },
  },
  secondCard: {
    mt: { xs: "45px", md: 0, lg: "31px", xl: "22px" },
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "center" },
    justifyContent: "center",
  },
  thirdText: {
    fontSize: { xs: "24px", md: "17px", lg: "24px", xl: "24px" },
    lineHeight: { xs: "36px", md: "30px", lg: "32px", xl: "36px" },
    fontFamily: theme.typography.anaHeim.regular,
    color: theme.palette.blue.main,
    maxWidth: { xs: "250px", md: "350px", lg: "515px", xl: "518px" },
    height: { xs: "180px", md: "inherit", lg: "inherit", xl: "108px" },
    textAlign: { xs: "center", md: "start" },
    mt: { xs: "16px" },
    ml: { md: "13px" },
  },
  img: {
    borderRadius: "0 0 200px 0",
  },
  line: {
    width: "25px",
    border: `1px solid ${theme.palette.red.main}`,
    marginRight: "8px",
    height: 0,
    backgroundColor: theme.palette.red.main,
  },
  greenCheck: {
    width: "21px",
    height: "21px",
  },
});

const BeforeStarting = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMediaQuery("(max-width:899px)");
  const { pathname } = useLocation();
  const { backgroundImg, title, subtitle, greenCheckImg, list } =
    useTranslation(pathname, "BeforeStarting");

  const xs = useMediaQuery(
    json2mq({
      maxWidth: 899,
    })
  );
  const md = useMediaQuery(
    json2mq({
      maxWidth: 1200,
      minWidth: 900,
    })
  );
  const lg = useMediaQuery(
    json2mq({
      maxWidth: 1535,
      minWidth: 1201,
    })
  );
  const xl = useMediaQuery(
    json2mq({
      minWidth: 1536,
    })
  );

  const stylesHelper = () => {
    if (xs) return { img: { height: "450px", width: "100vw" } };
    if (md) return { img: { height: "500px", width: "470px" } };
    if (lg) return { img: { height: "600px", width: "600px" } };
    if (xl) return { img: { height: "729px", width: "860px" } };
  };

  return (
    <Box sx={classes.container}>
      <Box>
        <img
          src={backgroundImg.src}
          alt={backgroundImg.alt}
          style={{ ...classes.img, ...(stylesHelper() && stylesHelper().img) }}
        />
      </Box>

      <Box sx={classes.child}>
        <Box sx={classes.titleContainer}>
          {isMobile ? null : <div style={classes.line}></div>}
          <Typography sx={classes.title}>{title}</Typography>
        </Box>
        <Typography sx={classes.subtitle}>{subtitle}</Typography>
        <Box sx={classes.firstCard}>
          <img
            src={greenCheckImg.src}
            alt={greenCheckImg.alt}
            style={classes.greenCheck}
          />
          <Typography
            sx={{
              ...classes.text,
              height: { xs: "144px", md: "inherit", xl: "72px" },
            }}
          >
            {list[0]}
          </Typography>
        </Box>
        <Box sx={classes.secondCard}>
          <img src={greenCheckImg.src} alt={greenCheckImg.alt} />
          <Typography
            sx={{
              ...classes.text,
              height: { xs: "108px", md: "inherit", xl: "72px" },
            }}
          >
            {list[1][0]}
            {xs && <br />}
            {list[1][1]}
          </Typography>
        </Box>
        <Box sx={classes.secondCard}>
          <img src={greenCheckImg.src} alt={greenCheckImg.alt} />
          <Typography
            sx={{
              ...classes.text,
              height: { xs: "108px", md: "inherit", xl: "72px" },
            }}
          >
            {list[2]}
          </Typography>
        </Box>
        <AdmissionProcessButton />
      </Box>
    </Box>
  );
};

export default BeforeStarting;
